import type { Microsoft365BrowseAt, Microsoft365FileRowDto, Microsoft365Source } from '@dbrain/shared';
import { Icons } from '@dbrain/ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyZone } from '../../../api/hooks.js';
import { KindBadge } from '../../documents/DocumentParts.js';
import { formatBytes, humanTime } from '@dbrain/app-core/format';
import { rowView, type PickedFile, type RowSort, type SortKey } from './model.js';

const COLUMNS: SortKey[] = ['name', 'by', 'at', 'size'];

/**
 * The picker's table (MsBelgeSec): a real check box per file ("Seç: {ad}"; the name is its label), Ad · Değiştiren ·
 * Değiştirilme (newest first) · Boyut; folders, sites and libraries first, opened by their name; a blocked row is
 * greyed with its reason in words; once 20 are picked the other boxes wait. Outlook's rows name the sender and the
 * mail's date, with the mail's subject under the name. Names are text only: nothing here links out of Oppy.
 */
export function PickerTable({
  rows,
  source,
  picked,
  sort,
  onSort,
  onOpen,
  onToggle,
  maxMb,
  limitId,
}: {
  rows: Microsoft365FileRowDto[];
  source: Microsoft365Source;
  picked: ReadonlyMap<string, PickedFile>;
  sort: RowSort;
  onSort: (key: SortKey) => void;
  onOpen: (at: Microsoft365BrowseAt) => void;
  onToggle: (row: Microsoft365FileRowDto, on: boolean) => void;
  maxMb: number;
  /** the "at most 20" note, which describes a box that waits */
  limitId: string;
}) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const base = useId();
  const outlook = source === 'outlook';
  const header = (key: SortKey) => {
    if (outlook && key === 'by') return t('microsoft365.files.picker.columns.from');
    if (outlook && key === 'at') return t('microsoft365.files.picker.columns.date');
    return t(`microsoft365.files.picker.columns.${key}`);
  };
  return (
    <div className="m365-table-frame">
      <table className="m365-table" aria-label={t('microsoft365.files.picker.table')} data-testid="m365-picker-table">
        <colgroup>
          <col className="m365-table__w-check" />
          <col />
          <col className="m365-table__w-by" />
          <col className="m365-table__w-at" />
          <col className="m365-table__w-size" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" className="m365-table__check">
              <span className="ui-visually-hidden">{t('microsoft365.files.picker.columns.name')}</span>
            </th>
            {COLUMNS.map((key) => {
              const sorted = sort.key === key;
              return (
                <th key={key} scope="col" className={key === 'size' ? 'm365-table__num' : undefined} aria-sort={sorted ? (sort.desc ? 'descending' : 'ascending') : undefined}>
                  <button type="button" className={sorted ? 'm365-table__sort m365-table__sort--active' : 'm365-table__sort'} onClick={() => onSort(key)} data-testid={`m365-picker-sort-${key}`}>
                    <span>{header(key)}</span>
                    {sorted ? sort.desc ? <Icons.chevronDown size={14} /> : <Icons.chevronUp size={14} /> : null}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, n) => {
            const view = rowView(row, source, picked, maxMb);
            const boxId = `${base}-box-${n}`;
            const whyId = `${base}-why-${n}`;
            const selected = view.kind === 'file' && view.selected;
            const describedBy = view.kind === 'blocked' ? whyId : view.kind === 'file' && view.full ? limitId : undefined;
            return (
              <tr
                key={`${row.kind}-${row.name}-${n}`}
                className={selected ? 'm365-row m365-row--selected' : view.kind === 'blocked' ? 'm365-row m365-row--blocked' : 'm365-row'}
                data-testid="m365-picker-row"
                data-kind={row.kind}
                data-name={row.name}
                data-selected={selected ? 'true' : undefined}
                data-blocked={view.kind === 'blocked' ? view.block : undefined}
              >
                <td className="m365-table__check">
                  {view.kind === 'file' || view.kind === 'blocked' ? (
                    <input
                      id={boxId}
                      type="checkbox"
                      className="m365-table__box"
                      checked={selected}
                      disabled={view.kind === 'blocked' || view.full}
                      aria-label={t('microsoft365.files.picker.select', { name: row.name })}
                      aria-describedby={describedBy}
                      onChange={(e) => onToggle(row, e.target.checked)}
                      data-testid="m365-picker-select"
                    />
                  ) : null}
                </td>
                <td className="m365-table__name">
                  {view.kind === 'open' && row.open ? (
                    <button type="button" className="m365-table__open" onClick={() => row.open && onOpen(row.open)} data-testid="m365-picker-open">
                      <RowIcon kind={row.kind} />
                      <span className="m365-table__label">{row.name}</span>
                    </button>
                  ) : view.kind === 'inert' ? (
                    <span className="m365-table__file">
                      <RowIcon kind={row.kind} />
                      <span className="m365-table__label">{row.name}</span>
                    </span>
                  ) : (
                    <label htmlFor={boxId} className="m365-table__file">
                      <KindBadge kind={row.fileKind} name={row.name} />
                      <span className="m365-table__label">{row.name}</span>
                    </label>
                  )}
                  {row.shared || row.context || view.kind === 'blocked' ? (
                    <span className="m365-table__meta">
                      {row.shared ? <span className="m365-table__tag">{t('microsoft365.files.picker.shared')}</span> : null}
                      {row.context ? <span className="m365-table__context">{row.context}</span> : null}
                      {view.kind === 'blocked' ? (
                        <span className="m365-table__why" id={whyId}>
                          {t(`microsoft365.files.blocked.${view.block}`, { mb: maxMb })}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </td>
                <td className={row.by ? 'm365-table__by' : 'm365-table__by m365-table__none'}>{row.by ?? '—'}</td>
                <td className="m365-table__at">{row.at ? humanTime(row.at, i18n.language, t, false, zone) : '—'}</td>
                <td className="m365-table__num m365-table__size">
                  {row.kind === 'folder' && row.childCount !== null
                    ? t('microsoft365.files.picker.items', { count: row.childCount })
                    : row.size !== null
                      ? formatBytes(row.size, i18n.language)
                      : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RowIcon({ kind }: { kind: Microsoft365FileRowDto['kind'] }) {
  if (kind === 'site') return <Icons.organization size={18} />;
  if (kind === 'library') return <Icons.book size={18} />;
  if (kind === 'folder') return <Icons.workspace size={18} />;
  return <Icons.file size={18} />;
}
